import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import { FaUserEdit } from "react-icons/fa";
import "react-loading-skeleton/dist/skeleton.css";
import { url } from "../constants/url";

const ProfileSettings = () => {
  const [userData, setUserData] = useState(null); // Profile data from backend
  const [loading, setLoading] = useState(true); // Loading state
  const [error, setError] = useState(null); // Error state
  const [editMode, setEditMode] = useState(false); // Toggle between view and edit modes
  const [submitting, setSubmitting] = useState(false); // Update progress

  const navigate = useNavigate();

  // Fetch admin profile from the backend when the component mounts.
  useEffect(() => {
    axios
      .get(url + "/api/admin/profile", {
        headers: {
          Authorization: localStorage.getItem("token")
            ? `Bearer ${localStorage.getItem("token")}`
            : ""
        }
      })
      .then((response) => {
        if (response.data.profile) {
          setUserData(response.data.profile);
        } else {
          setUserData({ fullName: "", email: "", address: "", imageUrl: "" });
        }
      })
      .catch((error) => {
        if (error.response?.status === 401) {
          localStorage.clear();
          navigate("/login");
        } else {
          console.error("Error fetching profile:", error);
          setError(error);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  // Handler for input changes in edit mode.
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserData((prevData) => ({
      ...prevData,
      [name]: value
    }));
  };

  // Save updated profile to the backend.
  const handleSave = () => {
    setSubmitting(true);

    axios
      .put(url + "/api/admin/profile", userData, {
        headers: {
          Authorization: localStorage.getItem("token")
            ? `Bearer ${localStorage.getItem("token")}`
            : ""
        }
      })
      .then((response) => {
        // success path
        setUserData(response.data.profile);
        setEditMode(false);
      })
      .catch((error) => {
        // if unauthorized, clear auth and redirect
        if (error.response?.status === 401) {
          localStorage.clear();
          navigate("/login");
          return;
        }

        console.error("Error updating profile:", error);
        alert("There was an error updating your profile.");
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  if (loading) {
    return (
      <div className="p-4 md:p-6 bg-white rounded-3xl shadow-md">
        <h1 className="text-3xl font-semibold mb-6">Profile Settings</h1>
        {/* Skeleton loaders for text and image */}
        <div className="flex flex-col md:flex-row md:justify-between items-center">
          <Skeleton
            circle={true}
            height={150}
            width={150}
            className="mb-4 md:mb-0"
          />
          <div className="w-full space-y-4 md:ml-6">
            <Skeleton height={30} width="80%" />
            <Skeleton height={30} width="90%" />
            <Skeleton height={30} width="70%" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="p-4 text-red-500">Error: {error.message}</div>;
  }

  return (
    <div className="p-4 md:p-6 bg-white rounded-3xl shadow-md">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-semibold">Profile Settings</h1>
        {!editMode && (
          <button
            onClick={() => setEditMode(true)}
            className="flex items-center text-blue-500 hover:text-blue-600"
          >
            <FaUserEdit className="mr-2" /> Edit Profile
          </button>
        )}
      </div>
      <div className="flex flex-col md:flex-row md:justify-between items-center">
        {/* <div className="mb-4 md:mb-0">
          {userData.imageUrl ? (
            <img
              src={userData.imageUrl}
              alt="User"
              className="h-40 w-40 md:h-80 md:w-80 rounded-md object-cover"
            />
          ) : (
            <Skeleton circle={true} height={150} width={150} />
          )}
        </div> */}
        <div className="flex flex-col md:ml-6 w-full">
          {/* Full Name */}
          <div className="mb-4">
            <label className="block text-lg font-semibold">Name:</label>
            {editMode ? (
              <input
                type="text"
                name="fullName"
                value={userData.fullName}
                onChange={handleInputChange}
                className="w-full p-2 border rounded"
              />
            ) : (
              <p className="text-xl">{userData.fullName}</p>
            )}
          </div>
          {/* Email */}
          <div className="mb-4">
            <label className="block text-lg font-semibold">Email:</label>
            {editMode ? (
              <input
                type="email"
                name="email"
                value={userData.email}
                onChange={handleInputChange}
                className="w-full p-2 border rounded"
              />
            ) : (
              <p className="text-xl">{userData.email}</p>
            )}
          </div>
          {/* Address */}
          {/* <div className="mb-4">
            <label className="block text-lg font-semibold">Address:</label>
            {editMode ? (
              <input
                type="text"
                name="address"
                value={userData.address}
                onChange={handleInputChange}
                className="w-full p-2 border rounded"
              />
            ) : (
              <p className="text-xl">{userData.address}</p>
            )}
          </div> */}
          {editMode && (
            <div className="flex space-x-4 mt-4">
              <button
                onClick={handleSave}
                disabled={submitting}
                className="p-3 bg-blue-500 text-white font-semibold rounded-lg hover:bg-blue-600"
              >
                {submitting ? "Saving..." : "Save Profile"}
              </button>
              <button
                onClick={() => setEditMode(false)}
                className="p-3 bg-gray-300 text-black font-semibold rounded-lg hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileSettings;
